import api from '../../services/api';
import { ApiResponse, CVEvaluationResponse, PolicyQuestionRequest, PolicyQuestionResponse, PolicyUploadResponse, TechnicalAnswerEvaluateRequest, TechnicalAnswerEvaluateResponse } from '../../types';

/** Response when starting async CV evaluation */
export interface CVEvaluationAsyncResponse {
  job_id: string;
  status: string;
}

/** Response when polling async job status (pending/running: status only; completed: full data) */
export interface CVEvaluationStatusResponse {
  job_id?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  results?: CVEvaluationResponse['results'];
  executive_kpis?: CVEvaluationResponse['executive_kpis'];
  processing_time_seconds?: number;
}

export const hrApi = {
  evaluateCVs: async (formData: FormData): Promise<ApiResponse<CVEvaluationResponse>> => {
    const response = await api.post<ApiResponse<CVEvaluationResponse>>(
      '/api/hr/cv/evaluate',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /** Start async CV evaluation; returns job_id. Poll getCVEvaluationStatus(job_id) for result. */
  evaluateCVsAsync: async (formData: FormData): Promise<ApiResponse<CVEvaluationAsyncResponse>> => {
    const response = await api.post<ApiResponse<CVEvaluationAsyncResponse>>(
      '/api/hr/cv/evaluate/async',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /** Poll status of an async CV evaluation job. */
  getCVEvaluationStatus: async (jobId: string): Promise<ApiResponse<CVEvaluationStatusResponse | CVEvaluationResponse>> => {
    const response = await api.get<ApiResponse<CVEvaluationStatusResponse | CVEvaluationResponse>>(
      `/api/hr/cv/evaluate/status/${jobId}`
    );
    return response.data;
  },

  uploadPolicies: async (formData: FormData): Promise<ApiResponse<PolicyUploadResponse>> => {
    const response = await api.post<ApiResponse<PolicyUploadResponse>>(
      '/api/hr/policy/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  askPolicyQuestion: async (question: PolicyQuestionRequest): Promise<ApiResponse<PolicyQuestionResponse>> => {
    const response = await api.post<ApiResponse<PolicyQuestionResponse>>(
      '/api/hr/policy/ask',
      question
    );
    return response.data;
  },

  generateTechnicalQuestions: async (formData: FormData): Promise<ApiResponse<{ questions: string[] }>> => {
    const response = await api.post<ApiResponse<{ questions: string[] }>>(
      '/api/hr/technical/generate-questions',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  evaluateTechnicalAnswers: async (data: TechnicalAnswerEvaluateRequest): Promise<ApiResponse<TechnicalAnswerEvaluateResponse>> => {
    const response = await api.post<ApiResponse<TechnicalAnswerEvaluateResponse>>(
      '/api/hr/technical/evaluate-answers',
      data
    );
    return response.data;
  },
};
